<!-- Word Usage Modal -->
<div *ngIf="isOpen" class="modal-overlay" (click)="onClose()">
  <div class="modal-content" (click)="$event.stopPropagation()">
    <!-- Modal Header -->
    <div class="modal-header-fixed">
      <div>
        <h2 class="modal-title">{{ translations.wordUsageAndQuotas }}</h2>
        <p class="modal-subtitle">
          {{ translations.trackTranslationUsage }}
        </p>
      </div>
      <button (click)="onClose()" class="close-button">
        <svg
          class="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          ></path>
        </svg>
      </button>
    </div>

    <!-- Modal Content -->
    <div class="modal-body">
      <!-- Current Usage Stats -->
      <div *ngIf="currentStats && !editingSettings">
        <h3 class="text-lg font-medium text-gray-900 mb-4">{{ translations.currentUsage }}</h3>

        <!-- Usage Overview -->
        <div class="bg-gray-50 rounded-lg p-4 mb-4">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-baseline gap-2">
              <span class="text-sm font-medium text-gray-700">{{ translations.wordsUsage }}</span>
              <span class="text-sm text-gray-600">{{
                getUsageDisplay()?.percentageText
              }}</span>
            </div>
            <!-- Toggle Icon -->
            <button
              type="button"
              (click)="setToggleView(!showRemainingView)"
              class="flex items-center justify-center w-10 h-6 bg-gray-200 rounded-full transition-colors duration-200 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              [class.bg-blue-500]="showRemainingView"
              [class.bg-gray-200]="!showRemainingView"
              [title]="
                showRemainingView
                  ? translations.switchToTotalView
                  : translations.switchToRemainingView
              "
            >
              <div
                class="w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200"
                [class.translate-x-2]="showRemainingView"
                [class.translate-x-0]="!showRemainingView"
              >
                <!-- Icon inside toggle -->
                <div class="w-full h-full flex items-center justify-center">
                  <svg
                    *ngIf="showRemainingView"
                    class="w-2 h-2 text-green-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <svg
                    *ngIf="!showRemainingView"
                    class="w-2 h-2 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </button>
          </div>

          <!-- Progress Bar -->
          <div class="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div
              class="h-2 rounded-full transition-all duration-300"
              [class]="getProgressBarColor()"
              [style.width.%]="getProgressBarWidth()"
            ></div>
          </div>

          <div class="flex justify-between text-xs text-gray-500">
            <span>{{ getUsageDisplay()?.wordsText }}</span>
            <span class="font-medium" [class]="getUsageDisplay()?.statusColor">
              {{ getUsageDisplay()?.statusText }}
            </span>
          </div>
        </div>

        <!-- Detailed Stats -->
        <div class="grid grid-cols-1 gap-4">
          <div class="bg-blue-50 rounded-lg p-4">
            <div class="flex items-center mb-2">
              <svg
                class="w-5 h-5 text-blue-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                ></path>
              </svg>
              <h4 class="font-medium text-blue-900">{{ translations.words }}</h4>
            </div>
            <p class="text-2xl font-bold text-blue-700">
              {{ (currentStats.wordsUsed || 0).toLocaleString() }}
            </p>
            <p class="text-sm text-blue-600">
              {{ translations.of }} {{ (currentSettings?.wordLimit || 0).toLocaleString() }}
            </p>
          </div>
        </div>

        <!-- Reset Info -->
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
          <div class="flex items-center">
            <svg
              class="w-5 h-5 text-yellow-600 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <span class="text-sm text-yellow-800">
              {{ translations.lastReset }} {{ currentStats.lastResetDate || "N/A" }}
            </span>
          </div>
        </div>
      </div>

      <!-- Settings Editor -->
      <div *ngIf="editingSettings" class="mt-6">
        <h3 class="text-lg font-medium text-gray-900 mb-4">{{ translations.quotaSettings }}</h3>

        <div class="space-y-4">
          <!-- Enable/Disable Quotas -->
          <div class="flex items-center justify-between">
            <div>
              <label class="text-sm font-medium text-gray-700"
                >{{ translations.enableWordQuotas }}</label
              >
              <p class="text-xs text-gray-500">{{ translations.setLimitsOnWordUsage }}</p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                [(ngModel)]="editForm.enabled"
                class="sr-only peer"
              />
              <div
                class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"
              ></div>
            </label>
          </div>

          <!-- Word Limit -->
          <div *ngIf="editForm.enabled">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ translations.wordLimit }}
            </label>
            <input
              type="number"
              [(ngModel)]="editForm.wordLimit"
              min="20"
              max="20000"
              step="10"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., 2000"
            />
            <p class="text-xs text-gray-500 mt-1">
              {{ translations.maximumWordsPerResetPeriod }}
            </p>
          </div>

          <!-- Reset Period -->
          <div *ngIf="editForm.enabled">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ translations.resetPeriod }}
            </label>
            <select
              [(ngModel)]="editForm.resetPeriod"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="daily">{{ translations.daily }}</option>
              <option value="weekly">{{ translations.weekly }}</option>
              <option value="monthly">{{ translations.monthly }}</option>
              <option value="never">{{ translations.neverManualResetOnly }}</option>
            </select>
            <p class="text-xs text-gray-500 mt-1">
              {{ translations.howOftenUsageStatsReset }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
